import type { MaybeRefOrGetter, Ref } from 'vue'
import { inject, isRef, nextTick, toValue, UIDIALOG_SEQUENCE_KEY, DIALOG_SEQUENCE_LOADER_MAX_MS } from '#imports'
import { until } from '@vueuse/core'

/** A query (anything with `isLoading`) or a plain promise. */
export type DialogSequenceHoldSource = { isLoading: Readonly<Ref<boolean>> } | PromiseLike<unknown>

export interface UseDialogSequenceHoldOptions {
    /** Name of the step this data feeds. */
    step: MaybeRefOrGetter<string>
    /** Whether the loaded data means that step will show. */
    when: () => boolean
}

/**
 * Keeps the dialog sequence on its loader until `sources` have loaded, so steps from sources that
 * answer at different times appear together instead of swapping in one by one.
 * Call it during setup, next to the queries a step depends on.
 *
 * Pass `step` when the step lives in a lazily loaded component: the hold then also lasts
 * until that step has registered, since its chunk loads only after the data arrived.
 */
export const useDialogSequenceHold = (sources: DialogSequenceHoldSource | DialogSequenceHoldSource[], options?: UseDialogSequenceHoldOptions): void => {
    const sequence = inject(UIDIALOG_SEQUENCE_KEY, null)

    // The server never shows the sheet, and each wait below arms a timer that would outlive the request.
    if (!sequence || import.meta.server) return

    const loaded = Promise.all((Array.isArray(sources) ? sources : [sources]).map(settled))

    if (!options) return sequence.hold(loaded)

    sequence.hold(loaded.then(async () => {
        await nextTick()
        await until(() => !options.when() || sequence.hasStep(toValue(options.step))).toBe(true, { timeout: DIALOG_SEQUENCE_LOADER_MAX_MS })
    }))
}

// A query's own promise resolves at once for client-only queries during hydration (their fetch is
// deferred until after it), so wait on its loading state instead.
function settled(source: DialogSequenceHoldSource): PromiseLike<unknown> {
    if ('isLoading' in source && isRef(source.isLoading)) {
        const { isLoading } = source

        return until(() => isLoading.value).toBe(false, { timeout: DIALOG_SEQUENCE_LOADER_MAX_MS })
    }

    return source as PromiseLike<unknown>
}
